
import React from "react";
import { useAssignment } from "@/context/AssignmentContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  RefreshCw,
  Repeat,
  BarChart3,
  PieChart as PieChartIcon,
  AwardIcon,
  CheckCircle,
  Clock,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { PageHeader } from "@/components/layouts/PageHeader";
import { StatCard } from "@/components/ui/StatCard";

const AssignmentStats: React.FC = () => {
  const { assignmentLogs, refreshAssignmentLogs } = useAssignment();

  const [activeMetric, setActiveMetric] = React.useState<'total' | 'rate'>('total');

  const totalAssignments = assignmentLogs.reduce((sum, stat) => sum + stat.totalAssignments, 0);
  const totalSuccessful = assignmentLogs.reduce((sum, stat) => sum + stat.successfulAssignments, 0);
  const totalRejected = assignmentLogs.reduce((sum, stat) => sum + stat.rejectedAssignments, 0);
  const totalPending = assignmentLogs.reduce((sum, stat) => sum + stat.pendingAssignments, 0);

  const overallSuccessRate = totalAssignments > 0
    ? ((totalSuccessful / totalAssignments) * 100).toFixed(1)
    : "0.0";

  const topLoadPartner = assignmentLogs.length > 0
    ? [...assignmentLogs].sort((a, b) => b.totalAssignments - a.totalAssignments)[0]
    : null;

  const concentrationRate = topLoadPartner && totalAssignments > 0
    ? Math.round((topLoadPartner.totalAssignments / totalAssignments) * 100)
    : 0;

  const riskCount = [
    totalPending > 0 ? 1 : 0,
    totalRejected > 0 ? 1 : 0,
    concentrationRate >= 45 ? 1 : 0,
  ].reduce((sum, n) => sum + n, 0);

  const bestPerformer = assignmentLogs.length > 0
    ? [...assignmentLogs]
      .filter(stat => stat.totalAssignments > 0)
      .sort((a, b) =>
        (b.successfulAssignments / b.totalAssignments) -
        (a.successfulAssignments / a.totalAssignments)
      )[0]
    : null;

  const barChartData = assignmentLogs.map(stat => ({
    name: stat.partnerName.length > 12 ? stat.partnerName.substring(0, 12) + '...' : stat.partnerName,
    total: stat.totalAssignments,
    success: stat.successfulAssignments,
    pending: stat.pendingAssignments,
    rejected: stat.rejectedAssignments,
    rate: stat.totalAssignments > 0
      ? parseFloat((stat.successfulAssignments / stat.totalAssignments * 100).toFixed(1))
      : 0
  }));

  const pieChartData = [
    { name: 'Επιτυχημένα', value: totalSuccessful, color: '#10b981' },
    { name: 'Απορριφθείσες', value: totalRejected, color: '#f87171' },
    { name: 'Σε αναμονή', value: totalPending, color: '#60a5fa' },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Ανάθεση Leads"
        description="Αποτύπωση απόδοσης, ισορροπίας και κινδύνων στη ροή ανάθεσης"
        icon={<Repeat />}
        iconGradient="blue"
        actions={
          <Button onClick={() => refreshAssignmentLogs()} variant="outline" className="border-slate-200">
            <RefreshCw className="mr-2 h-4 w-4" /> Ανανέωση
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard value={totalAssignments}         title="Συνολικές Αναθέσεις" icon={<Repeat />}      variant="info"                                                     size="compact" />
        <StatCard value={totalSuccessful}          title="Επιτυχημένες"         icon={<CheckCircle />} variant="success"                                                  size="compact" />
        <StatCard value={totalPending}             title="Σε Αναμονή"           icon={<Clock />}       variant="warning"                                                  size="compact" />
        <StatCard value={`${overallSuccessRate}%`} title="Ποσοστό Επιτυχίας"   icon={<TrendingUp />}  variant="gradient" gradientColors="from-violet-500 to-purple-600" size="compact" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Λειτουργική Εικόνα</CardTitle>
            <CardDescription>Επισκόπηση ισορροπίας κατανομής</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm text-slate-600">Concentration top συνεργάτη</span>
              <span className={`font-semibold text-sm ${concentrationRate >= 60 ? 'text-rose-600' : concentrationRate >= 45 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {concentrationRate}%
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full transition-all ${concentrationRate >= 60 ? 'bg-rose-500' : concentrationRate >= 45 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(100, concentrationRate)}%` }}
              />
            </div>
            {topLoadPartner && (
              <p className="text-sm text-slate-600">
                Υψηλότερο load: <span className="font-medium text-slate-900">{topLoadPartner.partnerName}</span>
                {' '}({topLoadPartner.totalAssignments} αναθέσεις)
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Alerts</CardTitle>
            <CardDescription>Σημεία προς έλεγχο</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2.5">
            <div className={`flex items-center justify-between border-l-4 rounded-r-lg px-3 py-2 ${totalPending > 0 ? 'border-l-amber-400 bg-amber-50' : 'border-l-slate-200 bg-slate-50'}`}>
              <span className="text-sm text-slate-700">Pending</span>
              <span className={`font-bold ${totalPending > 0 ? 'text-amber-700' : 'text-slate-700'}`}>{totalPending}</span>
            </div>
            <div className={`flex items-center justify-between border-l-4 rounded-r-lg px-3 py-2 ${totalRejected > 0 ? 'border-l-rose-500 bg-rose-50' : 'border-l-slate-200 bg-slate-50'}`}>
              <span className="text-sm text-slate-700">Rejected</span>
              <span className={`font-bold ${totalRejected > 0 ? 'text-rose-700' : 'text-slate-700'}`}>{totalRejected}</span>
            </div>
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${riskCount > 0 ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
              {riskCount > 0 ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <CheckCircle className="h-4 w-4 shrink-0" />}
              {riskCount > 0 ? `${riskCount} ενεργά ρίσκα` : "Χωρίς ενεργά ρίσκα"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bars */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-blue-50">
              <BarChart3 className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Συνολική Κατανομή</CardTitle>
              <CardDescription>Αναλυτική εξέλιξη αναθέσεων</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-slate-700">Επιτυχημένες</span>
              <span className="text-sm font-medium text-emerald-600">{totalSuccessful} / {totalAssignments}</span>
            </div>
            <Progress
              value={totalAssignments > 0 ? (totalSuccessful / totalAssignments) * 100 : 0}
              className="h-2"
            />
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-slate-700">Απορριφθείσες</span>
              <span className="text-sm font-medium text-red-600">{totalRejected} / {totalAssignments}</span>
            </div>
            <Progress
              value={totalAssignments > 0 ? (totalRejected / totalAssignments) * 100 : 0}
              className="h-2"
            />
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-slate-700">Σε Αναμονή</span>
              <span className="text-sm font-medium text-blue-600">{totalPending} / {totalAssignments}</span>
            </div>
            <Progress
              value={totalAssignments > 0 ? (totalPending / totalAssignments) * 100 : 0}
              className="h-2"
            />
          </div>
        </CardContent>
      </Card>

      {/* Best Performer */}
      {bestPerformer && (
        <Card className="border-slate-200 bg-gradient-to-r from-amber-50 to-yellow-50">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-amber-100">
                <AwardIcon className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Κορυφαίος Συνεργάτης</CardTitle>
                <CardDescription>Υψηλότερο ποσοστό επιτυχίας</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{bestPerformer.partnerName}</h3>
                <p className="text-sm text-slate-600">
                  {bestPerformer.successfulAssignments} επιτυχίες από {bestPerformer.totalAssignments} αναθέσεις
                </p>
              </div>
              <div className="text-right">
                <span className="text-3xl font-bold text-emerald-600">
                  {bestPerformer.totalAssignments > 0
                    ? ((bestPerformer.successfulAssignments / bestPerformer.totalAssignments) * 100).toFixed(1)
                    : "0"}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-violet-50">
                <PieChartIcon className="h-4 w-4 text-violet-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Κατανομή Αναθέσεων</CardTitle>
                <CardDescription>Ανά κατηγορία αποτελέσματος</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={4}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ stroke: '#94a3b8' }}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-50">
                <BarChart3 className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Επιδόσεις Συνεργατών</CardTitle>
                <CardDescription>Συγκριτική απόδοση</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeMetric} onValueChange={(v) => setActiveMetric(v as 'total' | 'rate')}>
              <TabsList className="bg-slate-100 p-1 rounded-lg mb-4">
                <TabsTrigger value="total" className="rounded-md data-[state=active]:bg-white">Συνολικά</TabsTrigger>
                <TabsTrigger value="rate" className="rounded-md data-[state=active]:bg-white">Ποσοστό</TabsTrigger>
              </TabsList>

              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  {activeMetric === 'total' ? (
                    <BarChart data={barChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                      <Legend />
                      <Bar dataKey="success" name="Επιτυχημένα" fill="#10b981" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="pending" name="Σε αναμονή" fill="#60a5fa" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="rejected" name="Απορριφθείσες" fill="#f87171" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  ) : (
                    <BarChart data={barChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <Tooltip
                        formatter={(value) => [`${value}%`, "Ποσοστό Επιτυχίας"]}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                      />
                      <Bar dataKey="rate" name="Ποσοστό %" fill="#a78bfa" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Αναλυτικά Στατιστικά</CardTitle>
          <CardDescription>Πλήρης πίνακας αναθέσεων ανά συνεργάτη</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableCaption>Συγκεντρωτικά στατιστικά αναθέσεων ανά συνεργάτη</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Συνεργάτης</TableHead>
                <TableHead className="text-right">Συνολικές</TableHead>
                <TableHead className="text-right">Επιτυχημένες</TableHead>
                <TableHead className="text-right">Απορριφθείσες</TableHead>
                <TableHead className="text-right">Σε Αναμονή</TableHead>
                <TableHead className="text-right">Ποσοστό</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignmentLogs.length > 0 ? (
                assignmentLogs.map((stat) => (
                  <TableRow key={stat.partnerId}>
                    <TableCell className="font-medium">{stat.partnerName}</TableCell>
                    <TableCell className="text-right">{stat.totalAssignments}</TableCell>
                    <TableCell className="text-right text-emerald-600">{stat.successfulAssignments}</TableCell>
                    <TableCell className="text-right text-red-600">{stat.rejectedAssignments}</TableCell>
                    <TableCell className="text-right text-blue-600">{stat.pendingAssignments}</TableCell>
                    <TableCell className="text-right font-medium">
                      {stat.totalAssignments > 0
                        ? `${((stat.successfulAssignments / stat.totalAssignments) * 100).toFixed(1)}%`
                        : "N/A"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                    Δεν υπάρχουν στατιστικά αναθέσεων ακόμα.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="border-slate-200 bg-slate-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Οδηγίες Χρήσης</CardTitle>
          <CardDescription>Πώς λειτουργεί η ανάθεση στο τρέχον σύστημα</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm space-y-3 text-slate-600">
            <p>
              Η ανάθεση είναι <strong className="text-slate-900">distance-first</strong> και όταν υπάρχουν ισοδύναμοι
              υποψήφιοι εφαρμόζεται <strong className="text-slate-900">round-robin tie-break</strong>.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Προτεραιότητα έχει η γεωγραφική εγγύτητα και το service match</li>
              <li>Λαμβάνονται υπόψη μόνο ενεργοί συνεργάτες εντός ακτίνας</li>
              <li>Σε ισοπαλία απόστασης, ενεργοποιείται rotation για δίκαιη διανομή</li>
              <li>Η ειδοποίηση partner γίνεται με βάση ενεργά κανάλια (SMS/Email)</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AssignmentStats;
